import { DialogActions, DialogShell } from './dialog';

/**
 * What happens when renewing the access token fails.
 *
 * Every screen asks for a token before its first query, and a renewal that
 * failed used to reach the page as whatever `auth0-spa-js` threw. The
 * production planner printed one of those into its error strip verbatim:
 *
 *   Missing Refresh Token (audience: 'https://api.dev.iqd-pro.com',
 *   scope: 'openid profile email offline_access')
 *
 * — a sentence about OAuth internals, in a red box, on a page about
 * installation dates. Sixty-odd screens render `error.message` the same way,
 * so this belongs where the token is fetched rather than in any of them: both
 * apps' `Auth0Bridge` wrap `getAccessTokenSilently` with it.
 */

/**
 * The Auth0 error codes that mean "this session is over, sign in again".
 *
 * All of them arrive as a `GenericError`, which carries the code on `error`
 * rather than in the class name — `MissingRefreshTokenError` is the one
 * development hits, because localhost holds a refresh token rather than
 * renewing through an iframe; `login_required` is the deployed equivalent,
 * thrown when the iframe comes back without a session. `invalid_grant` covers
 * a refresh token that has been rotated out or revoked, which these tenants do
 * eagerly: the rotation overlap is zero, so any reuse takes the whole family
 * down.
 *
 * Nothing else belongs here. A timeout or a dead network is not a signed-out
 * session, and sending someone to Auth0 over one would bounce them straight
 * back to a page that fails the same way.
 */
const RE_LOGIN_CODES = new Set([
  'missing_refresh_token',
  'login_required',
  'consent_required',
  'interaction_required',
  'invalid_grant',
]);

export function needsReLogin(error: unknown): boolean {
  if (typeof error !== 'object' || error === null || !('error' in error)) return false;
  const code = (error as { error: unknown }).error;
  return typeof code === 'string' && RE_LOGIN_CODES.has(code);
}

/**
 * What the screens are given instead of the SDK's error.
 *
 * The dialog is the real answer, but a page that puts the rejection in its own
 * error strip should read as a sentence rather than as a stack trace, and a
 * page that swallows it should still leave something sensible behind.
 */
export class SessionExpiredError extends Error {
  constructor() {
    super('Your session has expired. Sign in again to continue.');
    this.name = 'SessionExpiredError';
  }
}

export function SessionExpiredDialog({
  open,
  onClose,
  onSignIn,
}: {
  open: boolean;
  onClose: () => void;
  onSignIn: () => void;
}) {
  return (
    <DialogShell
      open={open}
      title="Session expired"
      onClose={onClose}
      width="max-w-md"
      footer={
        <DialogActions
          onCancel={onClose}
          onConfirm={onSignIn}
          cancelLabel="Not now"
          confirmLabel="Sign in again"
          // Nothing here waits on a request — the redirect leaves the page —
          // so the busy label is never reached.
          busyLabel="Sign in again"
        />
      }
    >
      <p className="text-sm text-hilevel-slate">
        Your sign-in could not be renewed, so this page has stopped loading data.
      </p>
      {/* Signing in leaves the document for Auth0 and comes back to a fresh
          load, which the estimator's unsaved-changes guard cannot hold — it
          blocks navigations inside the router, not the whole page. So this
          says plainly what is about to happen rather than taking the
          decision. */}
      <p className="text-sm text-hilevel-gray">
        Signing in reloads this page. Save anything you have part-written first.
      </p>
    </DialogShell>
  );
}
